/**
 * Summary Generator — Produces scientific narrative summaries from
 * workflow provenance data via the chat LLM endpoint.
 *
 * Entry point: generateSummary(provenance, workflow, workspace)
 *
 * Sends serialized provenance and workspace context to /services/agents/chat/
 * and parses the Markdown response into structured sections.
 *
 * @module gibbs/workflow/summaryGenerator
 */

import { buildMessages, serializeProvenance } from './summaryPrompt';

// ─── Constants ──────────────────────────────────────────────────────────────

/** Default chat endpoint. Overridable via setChatEndpoint() for testing. */
let chatEndpoint = '/services/agents/chat/';

/** Default timeout for summary generation (60s — summaries can be long). */
const DEFAULT_TIMEOUT_MS = 60000;

/**
 * Override the chat endpoint (for testing).
 *
 * @param {string} url
 */
export function setChatEndpoint(url) {
    chatEndpoint = url;
}

// ─── Section Parsing ────────────────────────────────────────────────────────

/**
 * Known section headers mapped to field names.
 * @type {Map<string, string>}
 */
const SECTION_MAP = new Map([
    ['objective', 'objective'],
    ['methods', 'methods'],
    ['results', 'results'],
    ['key findings', 'keyFindings'],
    ['key-findings', 'keyFindings'],
    ['keyfindings', 'keyFindings'],
]);

/**
 * Parse a Markdown response into structured sections.
 *
 * Splits by ## headers and maps known headers to named fields.
 *
 * @param {string} markdown - Raw Markdown response from the LLM
 * @returns {Object} { objective, methods, results, keyFindings, raw }
 */
export function parseSections(markdown) {
    const result = {
        objective: null,
        methods: null,
        results: null,
        keyFindings: null,
        raw: markdown,
    };

    if (!markdown) return result;

    // Split by ## headers
    const parts = markdown.split(/^##\s+/m);

    for (const part of parts) {
        if (!part.trim()) continue;

        // First line is the header, rest is content
        const newlineIdx = part.indexOf('\n');
        if (newlineIdx === -1) continue;

        const header = part.slice(0, newlineIdx).trim().toLowerCase();
        const content = part.slice(newlineIdx + 1).trim();

        const fieldName = SECTION_MAP.get(header);
        if (fieldName && content) {
            result[fieldName] = content;
        }
    }

    return result;
}

// ─── Fallback Summary ───────────────────────────────────────────────────────

/**
 * Build a minimal fallback summary from provenance data alone (no LLM).
 *
 * Used when the LLM is unavailable or the provenance log is empty.
 *
 * @param {import('./provenance.js').ProvenanceLog} provenance
 * @param {Object} [registry]
 * @returns {Object} { objective, methods, results, keyFindings, raw }
 */
function buildFallbackSummary(provenance, registry) {
    const steps = provenance.getSteps();

    if (steps.length === 0) {
        const raw = '## Results\n\nNo workflow steps were recorded.';
        return {
            objective: null,
            methods: null,
            results: 'No workflow steps were recorded.',
            keyFindings: null,
            raw,
        };
    }

    const provenanceText = serializeProvenance(provenance, registry);
    const succeeded = steps.filter((s) => s.status === 'SUCCEEDED').length;
    const failed = steps.filter((s) => s.status === 'FAILED').length;
    const totalDuration = steps.reduce((sum, s) => sum + (s.durationMs || 0), 0);
    const durationStr = totalDuration < 1000
        ? `${totalDuration}ms`
        : `${(totalDuration / 1000).toFixed(1)}s`;

    const raw = [
        '## Results',
        '',
        `Workflow executed ${steps.length} steps in ${durationStr}.`,
        `${succeeded} succeeded${failed > 0 ? `, ${failed} failed` : ''}.`,
        '',
        '## Methods',
        '',
        provenanceText,
    ].join('\n');

    return {
        objective: null,
        methods: provenanceText,
        results: `Workflow executed ${steps.length} steps in ${durationStr}. ${succeeded} succeeded${failed > 0 ? `, ${failed} failed` : ''}.`,
        keyFindings: null,
        raw,
    };
}

// ─── Main Entry Point ───────────────────────────────────────────────────────

/**
 * Generate a scientific summary from workflow provenance data.
 *
 * Sends provenance and workspace context to the chat endpoint and parses
 * the response into structured sections. Handles errors gracefully.
 *
 * @param {import('./provenance.js').ProvenanceLog} provenance
 * @param {Object} [workflow] - Workflow object (reserved for future use)
 * @param {Object} [workspace] - BMaps workspace state
 * @param {Object} [options]
 * @param {Object} [options.registry] - Registry for action name lookup
 * @param {number} [options.timeoutMs] - Request timeout in ms
 * @returns {Promise<Object>} Summary with sections and error info
 */
export async function generateSummary(provenance, workflow, workspace, options = {}) {
    const { registry, timeoutMs = DEFAULT_TIMEOUT_MS } = options;

    // Empty provenance — return fallback without calling LLM
    if (provenance.stepCount === 0) {
        const fallback = buildFallbackSummary(provenance, registry);
        return { ...fallback, error: null, partial: false };
    }

    // Build messages
    const messages = buildMessages(provenance, workspace, registry);

    // Send to chat endpoint
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    try {
        const response = await fetch(chatEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ messages }),
            signal: controller.signal,
        });

        clearTimeout(timer);

        if (!response.ok) {
            const fallback = buildFallbackSummary(provenance, registry);
            return {
                ...fallback,
                error: `HTTP ${response.status}: ${response.statusText}`,
                partial: true,
            };
        }

        const data = await response.json();

        if (data.error) {
            const fallback = buildFallbackSummary(provenance, registry);
            return {
                ...fallback,
                error: data.error,
                partial: true,
            };
        }

        const content = data.response || '';
        if (!content.trim()) {
            const fallback = buildFallbackSummary(provenance, registry);
            return {
                ...fallback,
                error: 'Empty response from LLM',
                partial: true,
            };
        }

        const sections = parseSections(content);
        return { ...sections, error: null, partial: false };
    } catch (err) {
        clearTimeout(timer);

        const isTimeout = err.name === 'AbortError';
        const errorMsg = isTimeout
            ? `Summary generation timed out after ${timeoutMs}ms`
            : `Network error: ${err.message}`;

        const fallback = buildFallbackSummary(provenance, registry);
        return {
            ...fallback,
            error: errorMsg,
            partial: true,
        };
    }
}
